import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

// ── Types (match admin_subscriptions.py response shapes exactly) ──────────
interface OverviewResponse {
  total_users: number;
  tier_breakdown: { free: number; pro: number; scholar_plus: number };
  trial_active: number;
  founding_members: { count: number; slots_remaining: number; is_open: boolean };
  transactions: { total: number; completed: number; pending: number; failed: number };
  revenue: { total_lkr: number; this_month_lkr: number };
}

interface SubscriberRow {
  user_id: string;
  name: string;
  email: string;
  tier: string;
  billing_cycle: string | null;
  subscription_expires_at: string | null;
  subscription_started_at: string | null;
  founding_member_number: number | null;
  trial_used: boolean;
  is_trial_active: boolean;
  days_until_expiry: number | null;
}
interface SubscriberListResponse { total: number; page: number; per_page: number; users: SubscriberRow[]; }

interface TransactionRow {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  tier: string;
  billing_cycle: string | null;
  amount_lkr: number;
  original_amount_lkr: number;
  discount_pct: number;
  is_founding_discount: boolean;
  founding_member_number: number | null;
  payhere_order_id: string | null;
  payhere_payment_id: string | null;
  status: string;
  paid_at: string | null;
  created_at: string | null;
}
interface TransactionListResponse { total: number; transactions: TransactionRow[]; }

interface FoundingMemberRow {
  founding_member_number: number | null;
  user_id: string;
  name: string;
  email: string;
  tier: string;
  joined_at: string | null;
}
interface FoundingResponse {
  total: number;
  counter: { current_count: number; is_open: boolean; remaining: number };
  members: FoundingMemberRow[];
}

interface PromoCodeRow {
  id: string;
  code: string;
  discount_pct: number;
  start_date: string | null;
  end_date: string | null;
  max_uses: number | null;
  uses_count: number;
  is_active: boolean;
  is_currently_valid: boolean;
}
interface PromoResponse { promo_codes: PromoCodeRow[]; }

interface Toast { id: number; message: string; type: 'success' | 'error'; }

type TabKey = 'subscribers' | 'transactions' | 'founding' | 'promos';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'subscribers', label: '⭐ Subscribers' },
  { key: 'transactions', label: '🧾 Transactions' },
  { key: 'founding', label: '🏅 Founding Members' },
  { key: 'promos', label: '🎟️ Promo Codes' },
];

const PER_PAGE = 20;

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtLKR = (n: number | null | undefined) =>
  `LKR ${Number(n ?? 0).toLocaleString('en-LK', { maximumFractionDigits: 2 })}`;

const fmtDate = (ts: string | null | undefined) => {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }).format(new Date(ts));
  } catch { return ts; }
};

const TIER_STYLE: Record<string, string> = {
  free: 'bg-gray-100 text-gray-600',
  pro: 'bg-teal-100 text-teal-700',
  scholar_plus: 'bg-purple-100 text-purple-700',
};
const tierLabel = (t: string) =>
  t === 'scholar_plus' ? 'Scholar+' : t ? t.charAt(0).toUpperCase() + t.slice(1) : '—';

const STATUS_STYLE: Record<string, string> = {
  completed: 'bg-green-100 text-green-700',
  pending: 'bg-amber/15 text-amber',
  failed: 'bg-red-100 text-red-700',
  admin_override: 'bg-purple-100 text-purple-700',
};

// ── Toast Container ────────────────────────────────────────────────────────
const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border cursor-pointer min-w-[280px]
          ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
      </div>
    ))}
  </div>
);

// ── Stat card ──────────────────────────────────────────────────────────────
const StatCard: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string }> = ({ label, value, sub, accent }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="mt-1 text-2xl font-bold" style={accent ? { color: accent } : { color: '#111827' }}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
  </div>
);

// ── Simple pagination ──────────────────────────────────────────────────────
const Pager: React.FC<{ page: number; totalPages: number; onPrev: () => void; onNext: () => void }> = ({ page, totalPages, onPrev, onNext }) => (
  <div className="flex items-center justify-between pt-2">
    <button
      onClick={onPrev}
      disabled={page === 1}
      className="px-4 py-2 rounded-xl text-sm font-semibold bg-teal-600 text-white hover:bg-teal-700
        disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150 shadow-sm"
    >
      ← Previous
    </button>
    <span className="text-sm text-gray-500 font-medium">Page {page} of {totalPages}</span>
    <button
      onClick={onNext}
      disabled={page >= totalPages}
      className="px-4 py-2 rounded-xl text-sm font-semibold bg-teal-600 text-white hover:bg-teal-700
        disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150 shadow-sm"
    >
      Next →
    </button>
  </div>
);

const TableSkeleton: React.FC = () => (
  <div className="space-y-2 animate-pulse">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="h-12 bg-white rounded-lg border border-gray-100 shadow-sm" />
    ))}
  </div>
);

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-16 gap-3">
    <span className="text-4xl">📭</span>
    <p className="text-gray-400 font-medium">{text}</p>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────
const Subscriptions: React.FC = () => {
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [tab, setTab] = useState<TabKey>('subscribers');

  const [subs, setSubs] = useState<SubscriberRow[]>([]);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsPage, setSubsPage] = useState(1);
  const [subsTier, setSubsTier] = useState('');
  const [subsSearch, setSubsSearch] = useState('');

  const [txns, setTxns] = useState<TransactionRow[]>([]);
  const [txnsTotal, setTxnsTotal] = useState(0);
  const [txnsPage, setTxnsPage] = useState(1);
  const [txnsStatus, setTxnsStatus] = useState('');

  const [members, setMembers] = useState<FoundingMemberRow[]>([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersPage, setMembersPage] = useState(1);

  const [promos, setPromos] = useState<PromoCodeRow[]>([]);

  const [tabLoading, setTabLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);
  const dismissToast = useCallback((id: number) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);

  // ── Overview ────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setOverviewLoading(true);
      try {
        const res = await adminApi.get<OverviewResponse>('/admin/subscriptions/overview');
        if (alive) setOverview(res.data);
      } catch {
        if (alive) addToast('Failed to load subscription overview.', 'error');
      } finally {
        if (alive) setOverviewLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [addToast]);

  // ── Tab data ────────────────────────────────────────────────────────────
  const fetchTab = useCallback(async () => {
    setTabLoading(true);
    try {
      if (tab === 'subscribers') {
        const params: Record<string, string | number> = { page: subsPage, per_page: PER_PAGE };
        if (subsTier) params.tier = subsTier;
        if (subsSearch) params.search = subsSearch;
        const res = await adminApi.get<SubscriberListResponse>('/admin/subscriptions/list', { params });
        setSubs(res.data.users ?? []);
        setSubsTotal(res.data.total ?? 0);
      } else if (tab === 'transactions') {
        const params: Record<string, string | number> = { page: txnsPage, per_page: PER_PAGE };
        if (txnsStatus) params.status = txnsStatus;
        const res = await adminApi.get<TransactionListResponse>('/admin/subscriptions/transactions', { params });
        setTxns(res.data.transactions ?? []);
        setTxnsTotal(res.data.total ?? 0);
      } else if (tab === 'founding') {
        const res = await adminApi.get<FoundingResponse>('/admin/founding-members', { params: { page: membersPage, per_page: PER_PAGE } });
        setMembers(res.data.members ?? []);
        setMembersTotal(res.data.total ?? 0);
      } else if (tab === 'promos') {
        const res = await adminApi.get<PromoResponse>('/admin/promo-codes');
        setPromos(res.data.promo_codes ?? []);
      }
    } catch {
      addToast('Failed to load data.', 'error');
    } finally {
      setTabLoading(false);
    }
  }, [tab, subsPage, subsTier, subsSearch, txnsPage, txnsStatus, membersPage, addToast]);

  useEffect(() => { fetchTab(); }, [fetchTab]);

  const subsPages = Math.max(1, Math.ceil(subsTotal / PER_PAGE));
  const txnsPages = Math.max(1, Math.ceil(txnsTotal / PER_PAGE));
  const membersPages = Math.max(1, Math.ceil(membersTotal / PER_PAGE));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">💳 Subscriptions</h1>
        <p className="text-sm text-gray-500 mt-1">Read-only view of subscription tiers, revenue, founding members and promo codes.</p>
      </div>

      {/* Overview cards */}
      {overviewLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-pulse">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 shadow-sm" />)}
        </div>
      ) : overview ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Users" value={overview.total_users.toLocaleString()} />
          <StatCard label="Pro" value={overview.tier_breakdown.pro.toLocaleString()} accent="#0D9488" sub={`Free ${overview.tier_breakdown.free.toLocaleString()}`} />
          <StatCard label="Scholar+" value={overview.tier_breakdown.scholar_plus.toLocaleString()} accent="#534AB7" sub={`Trials active ${overview.trial_active.toLocaleString()}`} />
          <StatCard
            label="Founding Members"
            value={overview.founding_members.count.toLocaleString()}
            accent="#BA7517"
            sub={`${overview.founding_members.slots_remaining.toLocaleString()} slots left · ${overview.founding_members.is_open ? 'Open' : 'Closed'}`}
          />
          <StatCard label="Revenue (Total)" value={fmtLKR(overview.revenue.total_lkr)} accent="#085041" />
          <StatCard label="Revenue (This Month)" value={fmtLKR(overview.revenue.this_month_lkr)} accent="#085041" />
          <StatCard label="Txns Completed" value={overview.transactions.completed.toLocaleString()} accent="#1D9E75" sub={`of ${overview.transactions.total.toLocaleString()} total`} />
          <StatCard label="Txns Pending / Failed" value={`${overview.transactions.pending} / ${overview.transactions.failed}`} accent="#D85A30" />
        </div>
      ) : null}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors duration-150 -mb-px border-b-2
              ${tab === t.key ? 'border-teal-600 text-teal-700 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters per tab */}
      {tab === 'subscribers' && (
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={subsTier}
            onChange={(e) => { setSubsTier(e.target.value); setSubsPage(1); }}
            className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm"
          >
            <option value="">All Paid Tiers</option>
            <option value="pro">Pro</option>
            <option value="scholar_plus">Scholar+</option>
          </select>
          <input
            type="text"
            value={subsSearch}
            onChange={(e) => { setSubsSearch(e.target.value); setSubsPage(1); }}
            placeholder="Search name or email…"
            className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm min-w-[240px]"
          />
          {!tabLoading && <span className="ml-auto text-sm text-gray-400">{subsTotal.toLocaleString()} subscribers</span>}
        </div>
      )}
      {tab === 'transactions' && (
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={txnsStatus}
            onChange={(e) => { setTxnsStatus(e.target.value); setTxnsPage(1); }}
            className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm"
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="admin_override">Admin Override</option>
          </select>
          {!tabLoading && <span className="ml-auto text-sm text-gray-400">{txnsTotal.toLocaleString()} transactions</span>}
        </div>
      )}

      {/* Tab body */}
      {tabLoading ? (
        <TableSkeleton />
      ) : (
        <>
          {/* Subscribers */}
          {tab === 'subscribers' && (
            subs.length === 0 ? <EmptyState text="No subscribers found." /> : (
              <div className="overflow-x-auto bg-white rounded-xl border border-gray-100 shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="px-4 py-3 font-semibold">User</th>
                      <th className="px-4 py-3 font-semibold">Tier</th>
                      <th className="px-4 py-3 font-semibold">Billing</th>
                      <th className="px-4 py-3 font-semibold">Started</th>
                      <th className="px-4 py-3 font-semibold">Expires</th>
                      <th className="px-4 py-3 font-semibold">Trial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map((u) => (
                      <tr key={u.user_id} className="border-b border-gray-50 hover:bg-gray-50/60">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{u.name}</div>
                          <div className="text-xs text-gray-400">{u.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${TIER_STYLE[u.tier] ?? 'bg-gray-100 text-gray-600'}`}>{tierLabel(u.tier)}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{u.billing_cycle ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{fmtDate(u.subscription_started_at)}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {fmtDate(u.subscription_expires_at)}
                          {typeof u.days_until_expiry === 'number' && (
                            <span className={`ml-1 text-xs ${u.days_until_expiry < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                              ({u.days_until_expiry}d)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {u.is_trial_active ? <span className="text-xs font-semibold text-teal-600">Active</span>
                            : u.trial_used ? <span className="text-xs text-gray-400">Used</span>
                            : <span className="text-xs text-gray-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Transactions */}
          {tab === 'transactions' && (
            txns.length === 0 ? <EmptyState text="No transactions found." /> : (
              <div className="overflow-x-auto bg-white rounded-xl border border-gray-100 shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="px-4 py-3 font-semibold">User</th>
                      <th className="px-4 py-3 font-semibold">Tier</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t) => (
                      <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{t.user_name}</div>
                          <div className="text-xs text-gray-400">{t.user_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${TIER_STYLE[t.tier] ?? 'bg-gray-100 text-gray-600'}`}>{tierLabel(t.tier)}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {fmtLKR(t.amount_lkr)}
                          {t.discount_pct > 0 && <span className="ml-1 text-xs text-gray-400">(-{t.discount_pct}%)</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[t.status] ?? 'bg-gray-100 text-gray-600'}`}>{t.status}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{fmtDate(t.paid_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Founding members */}
          {tab === 'founding' && (
            members.length === 0 ? <EmptyState text="No founding members yet." /> : (
              <div className="overflow-x-auto bg-white rounded-xl border border-gray-100 shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="px-4 py-3 font-semibold">#</th>
                      <th className="px-4 py-3 font-semibold">User</th>
                      <th className="px-4 py-3 font-semibold">Tier</th>
                      <th className="px-4 py-3 font-semibold">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.user_id} className="border-b border-gray-50 hover:bg-gray-50/60">
                        <td className="px-4 py-3 font-mono text-amber font-semibold">#{m.founding_member_number}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{m.name}</div>
                          <div className="text-xs text-gray-400">{m.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${TIER_STYLE[m.tier] ?? 'bg-gray-100 text-gray-600'}`}>{tierLabel(m.tier)}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{fmtDate(m.joined_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Promo codes */}
          {tab === 'promos' && (
            promos.length === 0 ? <EmptyState text="No promo codes created." /> : (
              <div className="overflow-x-auto bg-white rounded-xl border border-gray-100 shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="px-4 py-3 font-semibold">Code</th>
                      <th className="px-4 py-3 font-semibold">Discount</th>
                      <th className="px-4 py-3 font-semibold">Window</th>
                      <th className="px-4 py-3 font-semibold">Uses</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promos.map((p) => (
                      <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                        <td className="px-4 py-3 font-mono font-semibold text-gray-800">{p.code}</td>
                        <td className="px-4 py-3 text-gray-700">{p.discount_pct}%</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</td>
                        <td className="px-4 py-3 text-gray-600">{p.uses_count}{p.max_uses != null ? ` / ${p.max_uses}` : ''}</td>
                        <td className="px-4 py-3">
                          {p.is_currently_valid
                            ? <span className="inline-flex text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">Valid</span>
                            : <span className="inline-flex text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">{p.is_active ? 'Inactive window' : 'Disabled'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Pagination (per tab, where server-paginated) */}
          {tab === 'subscribers' && subsTotal > PER_PAGE && (
            <Pager page={subsPage} totalPages={subsPages} onPrev={() => setSubsPage((p) => Math.max(1, p - 1))} onNext={() => setSubsPage((p) => Math.min(subsPages, p + 1))} />
          )}
          {tab === 'transactions' && txnsTotal > PER_PAGE && (
            <Pager page={txnsPage} totalPages={txnsPages} onPrev={() => setTxnsPage((p) => Math.max(1, p - 1))} onNext={() => setTxnsPage((p) => Math.min(txnsPages, p + 1))} />
          )}
          {tab === 'founding' && membersTotal > PER_PAGE && (
            <Pager page={membersPage} totalPages={membersPages} onPrev={() => setMembersPage((p) => Math.max(1, p - 1))} onNext={() => setMembersPage((p) => Math.min(membersPages, p + 1))} />
          )}
        </>
      )}

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default Subscriptions;
